<template>
  <div class="flex items-center justify-between border-b border-gray-100 shadow-sm bg-white px-4 py-2">
    <!-- Left: menu toggle + banner -->
    <div class="flex items-center">
      <i
        @click="emit('toggle')"
        :class="expanded ? 'text-primary-600' : 'text-gray-700'"
        class="fa fa-bars text-xl cursor-pointer hover:text-primary-600 transition-colors"></i>
      <router-link class="ml-3" :to="{name:'user_profile'}">
        <img class="w-18 h-6" :src="bannerImage" alt="OBRS Banner" />
        <span class="text-[9px] bg-primary-600 p-1 rounded font-bold text-white">eRegistry Administration</span>
      </router-link>
    </div>

    <!-- Right: user account dropdown -->
    <Menu as="div" class="relative">
      <MenuButton
        id="profile-btn"
        class="flex items-center gap-2 hover:bg-gray-50 rounded-lg px-3 py-1.5 transition cursor-pointer focus:outline-none">
        <div class="w-9 h-9 bg-primary/10 rounded-full flex items-center justify-center shrink-0">
          <i class="fa-solid fa-user text-primary"></i>
        </div>
        <div id="user-details" v-if="userProfile?.givenName || userProfile?.email" class="text-left max-w-32 overflow-hidden">
          <p v-if="userProfile.givenName || userProfile.surName" class="text-gray-700 text-xs font-semibold leading-tight truncate">
            {{ userProfile.givenName }} {{ userProfile.surName }}
          </p>
          <p v-if="userProfile.email" class="text-gray-500 text-xs leading-tight truncate">
            {{ userProfile.email }}
          </p>
        </div>
        <i class="fa fa-chevron-down text-gray-400 text-xs shrink-0"></i>
      </MenuButton>

      <transition
        enter-active-class="transition ease-out duration-100"
        enter-from-class="transform opacity-0 scale-95"
        enter-to-class="transform opacity-100 scale-100"
        leave-active-class="transition ease-in duration-75"
        leave-from-class="transform opacity-100 scale-100"
        leave-to-class="transform opacity-0 scale-95">
        <MenuItems class="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg border border-gray-200 py-1 z-50 focus:outline-none">
          <MenuItem v-slot="{ active }">
            <button
              @click="profile"
              :class="['w-full text-left px-4 py-2 text-sm text-gray-700 transition-colors duration-150', active ? 'bg-gray-100' : '']">
              <i class="fa fa-user mr-2 text-gray-400"></i>
              Manage Account
            </button>
          </MenuItem>
          <div class="border-t border-gray-200"></div>
          <MenuItem v-slot="{ active }">
            <button
              @click="logout"
              :disabled="loggingOut"
              :class="['w-full text-left px-4 py-2 text-sm text-red-600 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed', active ? 'bg-red-50' : '']">
              <i class="fa fa-sign-out-alt mr-2 text-red-600"></i>
              {{ loggingOut ? 'Logging out...' : 'Logout' }}
            </button>
          </MenuItem>
        </MenuItems>
      </transition>
    </Menu>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/vue'
import { useLoginStore } from '@/stores/login'
import jwtService from "@/app/services/jwt.service"
import bannerImage from "@/assets/banner.png"
import { touchSession } from "@/composables/tokenRefresh"

// Types
interface UserProfile {
  givenName?: string
  surName?: string
  email?: string
  [key: string]: any
}

interface Props {
  expanded: boolean
}

defineProps<Props>()

const emit = defineEmits<{
  toggle: []
}>()

// Router and Store
const router = useRouter()
const loginStore = useLoginStore()
const { getUserLogout, getUserLogoutSuccess } = storeToRefs(loginStore)

// Reactive state
const userProfile = ref<UserProfile>({})
const loggingOut = ref<boolean>(false)

// Lifecycle
onMounted(() => {
  userProfile.value = jwtService.getUser()
})

// Methods
const logout = () => {
  loggingOut.value = true
  loginStore.doLogoutAccount()
    .finally(() => {
      loggingOut.value = false
    })
}

const profile = () => {
  const r = router.resolve({
    name: "profile",
  })
  window.location.assign(r.href)
}

const redirectToLogin = () => {
  sessionStorage.clear()
  touchSession()
  const r = router.resolve({
    name: "login",
  })
  window.location.assign(r.href)
}

// Watch for logout events
watch(getUserLogout, (value) => {
  if (value) redirectToLogin()
})

watch(getUserLogoutSuccess, (value) => {
  if (value) redirectToLogin()
})
</script>
